import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutContent } from '@/mocks/about';
import { Helmet } from '@/pages/home/components/Helmet';

export default function AboutPage() {
  return (
    <>
      <Helmet
        title="关于我们 | 国产专区 - 优质国货精选推荐平台"
        description="国产专区是国内领先的国货推荐与评测平台，致力于让更多人发现国货之美，支持中国品牌走向世界。了解我们的品牌故事、发展历程和联系方式。"
        keywords="关于国产专区,国货平台介绍,国产品牌推荐平台,联系我们"
        canonicalUrl="https://www.guochanzhuanqu.cn/about"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          'name': '关于国产专区',
          'description': '国产专区是国内领先的国货推荐与评测平台。',
          'url': 'https://www.guochanzhuanqu.cn/about',
          'inLanguage': 'zh-CN',
          'breadcrumb': {
            '@type': 'BreadcrumbList',
            'itemListElement': [
              { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'https://www.guochanzhuanqu.cn/' },
              { '@type': 'ListItem', 'position': 2, 'name': '关于我们', 'item': 'https://www.guochanzhuanqu.cn/about' },
            ],
          },
        }}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

        {/* Hero */}
        <section className="px-4 md:px-6 py-6" aria-labelledby="about-hero-title">
          <div className="max-w-4xl mx-auto">
            <div className="relative rounded-lg overflow-hidden h-[200px] md:h-[350px]">
              <img
                src={aboutContent.hero.image}
                alt="关于国产专区"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-background-900/70 to-transparent flex items-center">
                <div className="px-6 md:px-10">
                  <h1 id="about-hero-title" className="text-2xl md:text-3xl font-heading font-bold text-white">
                    {aboutContent.hero.title}
                  </h1>
                  <p className="mt-2 text-sm text-white/80">{aboutContent.hero.subtitle}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Brand Story */}
        <section className="px-4 md:px-6 py-10 md:py-14" aria-labelledby="story-title">
          <div className="max-w-4xl mx-auto">
            <h2 id="story-title" className="text-2xl font-heading font-bold text-foreground-900 mb-6">
              {aboutContent.intro.title}
            </h2>
            <div className="space-y-4">
              {aboutContent.intro.paragraphs.map((p, i) => (
                <p key={i} className="text-base text-foreground-700 leading-relaxed">
                  {p}
                </p>
              ))}
            </div>
          </div>
        </section>

        {/* Mission */}
        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100" aria-labelledby="mission-title">
          <div className="max-w-4xl mx-auto">
            <h2 id="mission-title" className="text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
              {aboutContent.mission.title}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
              {aboutContent.mission.items.map((item, i) => (
                <div key={i} className="bg-background-50 rounded-lg p-5">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 mb-3">
                    <i className={`${item.icon} text-xl text-primary-600`}></i>
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="px-4 md:px-6 py-10 md:py-14" aria-labelledby="milestones-title">
          <div className="max-w-3xl mx-auto">
            <h2 id="milestones-title" className="text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 md:-translate-x-px"></div>
              <div className="flex flex-col gap-6">
                {aboutContent.milestones.map((m, i) => (
                  <div key={i} className={`relative pl-12 md:pl-0 md:w-1/2 ${i % 2 === 0 ? 'md:pr-10 md:text-right' : 'md:ml-auto md:pl-10'}`}>
                    <div className="absolute left-2.5 md:left-auto top-1 w-3 h-3 rounded-full bg-accent-500 md:absolute md:top-1"
                      style={i % 2 === 0 ? { left: 'auto', right: '-7px' } : { left: '-7px' }}
                    ></div>
                    <span className="text-sm font-bold text-accent-600">{m.year}</span>
                    <p className="mt-1 text-sm text-foreground-600 leading-relaxed">{m.event}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100" id="testimonials" aria-labelledby="testimonials-title">
          <div className="max-w-4xl mx-auto">
            <h2 id="testimonials-title" className="text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {aboutContent.testimonials.map((t, i) => (
                <div key={i} className="bg-background-50 rounded-lg p-5 text-center">
                  <div className="w-16 h-16 mx-auto mb-3 rounded-full overflow-hidden">
                    <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4 italic">
                    "{t.content}"
                  </p>
                  <p className="text-sm font-heading font-semibold text-foreground-900">{t.name}</p>
                  <p className="text-xs text-foreground-500 mt-0.5">{t.role}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="px-4 md:px-6 py-10 md:py-14" id="contact" aria-labelledby="contact-title">
          <div className="max-w-4xl mx-auto">
            <h2 id="contact-title" className="text-2xl font-heading font-bold text-foreground-900 text-center mb-3">
              {aboutContent.contact.title}
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-8 max-w-lg mx-auto">
              {aboutContent.contact.description}
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="flex flex-col gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                    <i className="ri-mail-line text-lg text-primary-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                    <p className="text-sm text-foreground-600">{aboutContent.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                    <i className="ri-phone-line text-lg text-primary-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">联系电话</p>
                    <p className="text-sm text-foreground-600">{aboutContent.contact.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                    <i className="ri-map-pin-line text-lg text-primary-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">办公地址</p>
                    <p className="text-sm text-foreground-600">{aboutContent.contact.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                    <i className="ri-time-line text-lg text-primary-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">工作时间</p>
                    <p className="text-sm text-foreground-600">{aboutContent.contact.workingHours}</p>
                  </div>
                </div>
              </div>

              <form
                data-readdy-form
                className="bg-background-100 rounded-lg p-5 flex flex-col gap-4"
                action="https://readdy.ai/api/form/d9qqo26e3oq4jqumtp3g"
                method="POST"
                encType="application/x-www-form-urlencoded"
                onSubmit={async (e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const fd = new FormData(form);
                  const hnpt = (fd.get('phone_alt') as string || '').trim();
                  const statusEl = document.getElementById('contact-form-status');

                  if (hnpt) {
                    if (statusEl) {
                      statusEl.textContent = '感谢您的留言，我们会尽快回复！';
                      statusEl.className = 'text-sm text-center mt-2 text-green-600';
                    }
                    return;
                  }

                  fd.delete('phone_alt');

                  try {
                    const params = new URLSearchParams();
                    fd.forEach((val, key) => {
                      params.append(key, val as string);
                    });
                    const res = await fetch(form.action, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                      body: params.toString(),
                    });
                    const responseText = await res.text();
                    let parsed;
                    try { parsed = JSON.parse(responseText); } catch { parsed = null; }
                    const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '';

                    if (res.ok && parsed?.code === 'OK') {
                      if (statusEl) {
                        statusEl.textContent = '感谢您的留言，我们会尽快回复！';
                        statusEl.className = 'text-sm text-center mt-2 text-green-600';
                      }
                      form.reset();
                    } else {
                      if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
                        if (statusEl) {
                          statusEl.textContent = '感谢您的留言，我们会尽快回复！';
                          statusEl.className = 'text-sm text-center mt-2 text-green-600';
                        }
                      } else {
                        if (statusEl) {
                          statusEl.textContent = serverMsg || '提交失败，请稍后再试。';
                          statusEl.className = 'text-sm text-center mt-2 text-red-500';
                        }
                      }
                    }
                  } catch {
                    if (statusEl) {
                      statusEl.textContent = '网络错误，请稍后再试。';
                      statusEl.className = 'text-sm text-center mt-2 text-red-500';
                    }
                  }
                }}
              >
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1">姓名</label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    required
                    className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1">邮箱</label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    required
                    className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1">主题</label>
                  <input
                    type="text"
                    id="contact-subject"
                    name="subject"
                    required
                    className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-colors"
                    placeholder="请输入主题"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1">留言内容</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-colors resize-none"
                    placeholder="请输入您的留言（500字以内）"
                  ></textarea>
                </div>
                {/* Honeypot */}
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="hnpt-field"
                />
                <button
                  type="submit"
                  className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  发送留言
                </button>
                <p id="contact-form-status" className="text-sm text-center mt-0"></p>
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}